import { googleLogout } from '@react-oauth/google';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  accessTokenStorageName,
  AgreementRestSet,
  getInitialGeneralState,
  NotificationRestSet,
  TwoFactorRestSet,
  UserRestSet,
} from './general-slice-definitions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import {
  changePassword,
  createAgreement,
  createAgreementAcceptance,
  createCustomLocalization,
  createNotificationSubscription,
  createUpdateVersion,
  assignTorizonVersionSet,
  createTorizonVersionSet,
  createVersionSet,
  deleteAgreementById,
  deleteAgreementFileById,
  deleteCustomLocalizationById,
  deleteNotificationSubscriptionById,
  deleteUpdateVersionById,
  deleteTorizonVersionSetById,
  deleteVersionSetById,
  disableTwoFactor,
  getAgreementById,
  getAgreements,
  getCustomLocalizations,
  getFrontendParams,
  getNotificationSubscriptions,
  getReportDataSets,
  getTorizonPackages,
  getUpdateVersions,
  getAssignableRobots,
  getTorizonVersionSets,
  getUserByToken,
  getVersionSets,
  login,
  patchAgreementById,
  patchCustomLocalizationById,
  patchNotificationSubscriptionById,
  patchUpdateVersionById,
  patchTorizonVersionSetById,
  patchVersionSetById,
  postResetPasswordEmail,
  postSubscribeToOrganizationById,
  postSubscribeToRobotById,
  registerUser,
  resetUserTwoFactor,
  setPassword,
  uploadAgreementFile,
  validateUserInvite,
  verifyTwoFactor,
} from './general-actions';
import { removeToken } from '../../services/data/axios-base';
import UserRole from '../../models/enums/user-roles';
import CurrentUser, { defaultUserState } from '../../models/general/current-user';
import RestSet, { defaultRestSet } from '@common/interfaces/api/rest-set';
import { updateDataList } from '../../utils/generic-data-list-handlers';
import { updateAgreementOrders } from '../../models/enums/agreements';
import NotificationSubscription from '../../models/general/notifications/notification-trigger';

const initialGeneralState = getInitialGeneralState();

const generalSlice = createSlice({
  name: 'general',
  initialState: initialGeneralState,
  reducers: {
    resetGeneralState(state) {
      // Reset to initial admin state
      return { ...initialGeneralState };
    },
    localLogin(state, action: PayloadAction<string | null>) {
      if (action.payload != null) {
        state.accessToken.token = action.payload;
      }
      state.accessToken.localStorageChecked = true;
    },
    logout(state) {
      removeToken();
      googleLogout();
      return { ...initialGeneralState };
    },
    resetLoginState(state) {
      state.user.rest.postStatus = HttpFetchStatus.Idle;
      state.user.rest.postError = null;
    },
    resetUserRestStatus(state, action: PayloadAction<Partial<UserRestSet>>) {
      state.user.rest = { ...state.user.rest, ...action.payload };
    },
    setCustomUserRole(state, action: PayloadAction<UserRole | null>) {
      state.customUserRole = action.payload;
    },
    resetAgreementsState(state) {
      state.agreements = {
        data: null,
        selectedId: null,
        currentDetails: null,
        uploadProgress: null,
        rest: {
          ...defaultRestSet,
          deleteUploadFileError: null,
          deleteUploadFileStatus: HttpFetchStatus.Idle,
          postAcceptAgreementError: null,
          postAcceptAgreementStatus: HttpFetchStatus.Idle,
          getAgreementError: null,
          getAgreementStatus: HttpFetchStatus.Idle,
          postUploadFileError: null,
          postUploadFileStatus: HttpFetchStatus.Idle,
        },
      };
    },
    resetAgreementsRestState(state, action: PayloadAction<Partial<AgreementRestSet>>) {
      state.agreements.rest = { ...state.agreements.rest, ...action.payload };
    },
    resetVersionVersionsRestState(state, action: PayloadAction<Partial<RestSet>>) {
      state.versions.versions.rest = { ...state.versions.versions.rest, ...action.payload };
    },
    resetVersionSetsRestState(state, action: PayloadAction<Partial<RestSet>>) {
      state.versions.sets.rest = { ...state.versions.sets.rest, ...action.payload };
    },
    resetTorizonVersionSetsRestState(state, action: PayloadAction<Partial<RestSet>>) {
      state.versions.torizonSets.rest = { ...state.versions.torizonSets.rest, ...action.payload };
    },
    resetAssignableRobotsRestState(state, action: PayloadAction<Partial<RestSet>>) {
      state.versions.assignableRobots.rest = {
        ...state.versions.assignableRobots.rest,
        ...action.payload,
      };
    },
    setAgreementFileUploadProgress(state, action: PayloadAction<null | number>) {
      state.agreements.uploadProgress = action.payload;
    },
    setSelectedAgreementId(state, action: PayloadAction<string | null>) {
      state.agreements.selectedId = action.payload;
    },
    resetCustomLocalizationRestStatus(state, action: PayloadAction<Partial<RestSet>>) {
      state.customLocalizations.rest = { ...state.customLocalizations.rest, ...action.payload };
    },
    setSelectedCustomLocalizationId(state, action: PayloadAction<string | null>) {
      state.customLocalizations.selectedId = action.payload;
    },
    resetNotificationRestState(state, action: PayloadAction<Partial<NotificationRestSet>>) {
      state.notifications.rest = { ...state.notifications.rest, ...action.payload };
    },
    resetReportData(state) {
      state.notifications.reportDataSets = null;
    },
    setSelectedNotification(
      state,
      action: PayloadAction<Partial<NotificationSubscription> | null>,
    ) {
      state.notifications.selectedNotification = action.payload;
    },
    resetRegisterRestState(state) {
      state.userInviteData.rest = {
        getStatus: HttpFetchStatus.Idle,
        getError: null,
        postStatus: HttpFetchStatus.Idle,
        postError: null,
      };
    },
    setIsAdvancedModeEnabled(state, action: PayloadAction<boolean>) {
      state.isAdvancedModeEnabled = action.payload;
    },
    commitTwoFactorLogin(state, action: PayloadAction<{ token: string; user: CurrentUser }>) {
      // Final commit of a forced 2FA-setup login, called only once the user has seen and
      // acknowledged their backup codes (TwoFactorSetup screen 2). Until this is dispatched,
      // confirmTwoFactorSetup.fulfilled deliberately leaves accessToken/user/pendingTwoFactor
      // untouched so the app never prematurely redirects away from the backup codes screen.
      state.accessToken.token = action.payload.token;
      localStorage.setItem(accessTokenStorageName, action.payload.token);
      state.twoFactor.pending = null;
      state.user.data = action.payload.user;
    },
    resetTwoFactorRestState(state, action: PayloadAction<Partial<TwoFactorRestSet>>) {
      state.twoFactor.rest = { ...state.twoFactor.rest, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state, action) => {
      state.user.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.user.rest.postStatus = HttpFetchStatus.Success;
      state.user.rest.postError = null;
      if ('requires2fa' in action.payload) {
        state.twoFactor.pending = {
          token: action.payload.pendingToken,
          setupRequired: action.payload.setupRequired,
        };
        return;
      }
      state.accessToken.token = action.payload.token;
      localStorage.setItem(accessTokenStorageName, action.payload.token);
      state.twoFactor.pending = null;
      state.user.data = action.payload.user;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.user.rest.postStatus = HttpFetchStatus.Failed;
      state.user.rest.postError = action.payload ?? null;
    });
    builder.addCase(postResetPasswordEmail.pending, (state, action) => {
      state.user.rest.postResetPasswordEmailStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(postResetPasswordEmail.fulfilled, (state, action) => {
      state.user.rest.postResetPasswordEmailStatus = HttpFetchStatus.Success;
      state.user.rest.postResetPasswordEmailError = null;
    });
    builder.addCase(postResetPasswordEmail.rejected, (state, action) => {
      state.user.rest.postResetPasswordEmailStatus = HttpFetchStatus.Failed;
      state.user.rest.postResetPasswordEmailError = action.payload ?? null;
    });
    builder.addCase(verifyTwoFactor.pending, (state, action) => {
      state.twoFactor.rest.verifyTwoFactorPostStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(verifyTwoFactor.fulfilled, (state, action) => {
      state.twoFactor.rest.verifyTwoFactorPostStatus = HttpFetchStatus.Success;
      state.twoFactor.rest.verifyTwoFactorPostError = null;
      state.accessToken.token = action.payload.token;
      localStorage.setItem(accessTokenStorageName, action.payload.token);
      state.twoFactor.pending = null;
      state.user.data = action.payload.user;
    });
    builder.addCase(verifyTwoFactor.rejected, (state, action) => {
      state.twoFactor.rest.verifyTwoFactorPostStatus = HttpFetchStatus.Failed;
      state.twoFactor.rest.verifyTwoFactorPostError = action.payload ?? null;
    });
    builder.addCase(disableTwoFactor.pending, (state, action) => {
      state.twoFactor.rest.disableTwoFactorPostStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(disableTwoFactor.fulfilled, (state, action) => {
      state.twoFactor.rest.disableTwoFactorPostStatus = HttpFetchStatus.Success;
      state.twoFactor.rest.disableTwoFactorPostError = null;
    });
    builder.addCase(disableTwoFactor.rejected, (state, action) => {
      state.twoFactor.rest.disableTwoFactorPostStatus = HttpFetchStatus.Failed;
      state.twoFactor.rest.disableTwoFactorPostError = action.payload ?? null;
    });
    builder.addCase(resetUserTwoFactor.pending, (state, action) => {
      state.twoFactor.rest.resetUserTwoFactorPostStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(resetUserTwoFactor.fulfilled, (state, action) => {
      state.twoFactor.rest.resetUserTwoFactorPostStatus = HttpFetchStatus.Success;
      state.twoFactor.rest.resetUserTwoFactorPostError = null;
    });
    builder.addCase(resetUserTwoFactor.rejected, (state, action) => {
      state.twoFactor.rest.resetUserTwoFactorPostStatus = HttpFetchStatus.Failed;
      state.twoFactor.rest.resetUserTwoFactorPostError = action.payload ?? null;
    });
    builder.addCase(setPassword.pending, (state, action) => {
      state.user.rest.postSetPasswordStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(setPassword.fulfilled, (state, action) => {
      state.user.rest.postSetPasswordStatus = HttpFetchStatus.Success;
      state.user.rest.postSetPasswordError = null;
      if ('requires2fa' in action.payload) {
        state.twoFactor.pending = {
          token: action.payload.pendingToken,
          setupRequired: action.payload.setupRequired,
        };
        return;
      }
      state.accessToken.token = action.payload.token;
      localStorage.setItem(accessTokenStorageName, action.payload.token);
      state.twoFactor.pending = null;
      state.user.data = action.payload.user;
    });
    builder.addCase(setPassword.rejected, (state, action) => {
      state.user.rest.postSetPasswordStatus = HttpFetchStatus.Failed;
      state.user.rest.postSetPasswordError = action.payload ?? null;
    });
    builder.addCase(changePassword.pending, (state, action) => {
      state.user.rest.postChangePasswordStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(changePassword.fulfilled, (state, action) => {
      state.user.rest.postChangePasswordStatus = HttpFetchStatus.Success;
      state.user.rest.postChangePasswordError = null;
    });
    builder.addCase(changePassword.rejected, (state, action) => {
      state.user.rest.postChangePasswordStatus = HttpFetchStatus.Failed;
      state.user.rest.postChangePasswordError = action.payload ?? null;
    });
    builder.addCase(getUserByToken.pending, (state, action) => {
      state.user.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getUserByToken.fulfilled, (state, action) => {
      state.user.rest.getStatus = HttpFetchStatus.Success;
      state.user.rest.getError = null;
      state.user.data = action.payload;
    });
    builder.addCase(getUserByToken.rejected, (state, action) => {
      state.user.rest.getStatus = HttpFetchStatus.Failed;
      state.user.rest.getError = action.payload ?? null;
      state.user.data = defaultUserState;
      state.accessToken.token = null;
      removeToken();
    });

    builder.addCase(getAgreements.pending, (state, action) => {
      state.agreements.rest.getStatus = HttpFetchStatus.Loading;
      state.agreements.data = null;
    });
    builder.addCase(getAgreements.fulfilled, (state, action) => {
      state.agreements.rest.getStatus = HttpFetchStatus.Success;
      state.agreements.data = action.payload ?? null;
    });
    builder.addCase(getAgreements.rejected, (state, action) => {
      state.agreements.rest.getStatus = HttpFetchStatus.Failed;
      state.agreements.rest.getError = action.payload ?? null;
    });
    builder.addCase(getAgreementById.pending, (state, action) => {
      state.agreements.rest.getAgreementStatus = HttpFetchStatus.Loading;
      state.agreements.currentDetails = null;
    });
    builder.addCase(getAgreementById.fulfilled, (state, action) => {
      state.agreements.rest.getAgreementStatus = HttpFetchStatus.Success;
      state.agreements.currentDetails = action.payload ?? null;
    });
    builder.addCase(getAgreementById.rejected, (state, action) => {
      state.agreements.rest.getAgreementStatus = HttpFetchStatus.Failed;
      state.agreements.rest.getAgreementError = action.payload ?? null;
    });
    builder.addCase(createAgreement.pending, (state, action) => {
      state.agreements.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createAgreement.fulfilled, (state, action) => {
      state.agreements.rest.postStatus = HttpFetchStatus.Success;
      if (state.agreements.data != null) {
        state.agreements.data = updateDataList(action.payload, state.agreements.data);
        state.agreements.selectedId = action.payload.id;
      }
    });
    builder.addCase(createAgreement.rejected, (state, action) => {
      state.agreements.rest.postStatus = HttpFetchStatus.Failed;
      state.agreements.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchAgreementById.pending, (state, action) => {
      state.agreements.rest.patchStatus = HttpFetchStatus.Loading;
      state.agreements.rest.patchId = action.meta.arg.id;
    });
    builder.addCase(patchAgreementById.fulfilled, (state, action) => {
      state.agreements.rest.patchStatus = HttpFetchStatus.Success;
      if (state.agreements.data != null) {
        const oldAgreement = state.agreements.data.find((a) => a.id === action.payload.id);
        if (oldAgreement != null && oldAgreement.enabled && !action.payload.enabled) {
          updateAgreementOrders(state.agreements.data, oldAgreement);
        }
        state.agreements.data = updateDataList(action.payload, state.agreements.data);
      }
    });
    builder.addCase(patchAgreementById.rejected, (state, action) => {
      state.agreements.rest.patchStatus = HttpFetchStatus.Failed;
      state.agreements.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteAgreementById.pending, (state, action) => {
      state.agreements.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteAgreementById.fulfilled, (state, action) => {
      state.agreements.rest.deleteStatus = HttpFetchStatus.Success;
      if (state.agreements.data != null) {
        const oldAgreement = state.agreements.data.find((a) => a.id === action.payload);
        if (oldAgreement != null) {
          updateAgreementOrders(state.agreements.data, oldAgreement);
        }
        const agreementIndex = state.agreements.data.findIndex(
          (agreement) => agreement.id === action.payload,
        );
        if (agreementIndex !== -1) {
          state.agreements.data.splice(agreementIndex, 1);
          state.agreements.selectedId = null;
        }
      }
    });
    builder.addCase(deleteAgreementById.rejected, (state, action) => {
      state.agreements.rest.deleteStatus = HttpFetchStatus.Failed;
      state.agreements.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(uploadAgreementFile.pending, (state, action) => {
      state.agreements.rest.postUploadFileStatus = HttpFetchStatus.Loading;
      state.agreements.uploadProgress = 0;
    });
    builder.addCase(uploadAgreementFile.fulfilled, (state, action) => {
      state.agreements.rest.postUploadFileStatus = HttpFetchStatus.Success;
      state.agreements.uploadProgress = null;
      const agreement = state.agreements.data?.find((a) => a.id === state.agreements.selectedId);
      if (agreement != null) {
        agreement.files = action.payload.files;
      }
    });
    builder.addCase(uploadAgreementFile.rejected, (state, action) => {
      state.agreements.rest.postUploadFileStatus = HttpFetchStatus.Failed;
      state.agreements.rest.postUploadFileError = action.payload ?? null;
      state.agreements.uploadProgress = null;
    });
    builder.addCase(deleteAgreementFileById.pending, (state, action) => {
      state.agreements.rest.deleteUploadFileStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteAgreementFileById.fulfilled, (state, action) => {
      state.agreements.rest.deleteUploadFileStatus = HttpFetchStatus.Success;
      const agreement = state.agreements.data?.find((a) => a.id === state.agreements.selectedId);
      if (agreement != null) {
        const fileIndex = agreement.files.findIndex((f) => f.id === action.payload);
        if (fileIndex !== -1) {
          agreement.files.splice(fileIndex, 1);
        }
      }
    });
    builder.addCase(deleteAgreementFileById.rejected, (state, action) => {
      state.agreements.rest.deleteUploadFileStatus = HttpFetchStatus.Failed;
      state.agreements.rest.deleteUploadFileError = action.payload ?? null;
    });
    builder.addCase(createAgreementAcceptance.pending, (state, action) => {
      state.agreements.rest.postAcceptAgreementStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createAgreementAcceptance.fulfilled, (state, action) => {
      state.agreements.rest.postAcceptAgreementStatus = HttpFetchStatus.Success;
      state.user.data.agreementAcceptances.push(action.payload);
    });
    builder.addCase(createAgreementAcceptance.rejected, (state, action) => {
      state.agreements.rest.postAcceptAgreementStatus = HttpFetchStatus.Failed;
      state.agreements.rest.postAcceptAgreementError = action.payload ?? null;
    });

    builder.addCase(getCustomLocalizations.pending, (state, action) => {
      state.customLocalizations.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getCustomLocalizations.fulfilled, (state, action) => {
      state.customLocalizations.rest.getStatus = HttpFetchStatus.Success;
      state.customLocalizations.data = action.payload ?? null;
    });
    builder.addCase(getCustomLocalizations.rejected, (state, action) => {
      state.customLocalizations.rest.getStatus = HttpFetchStatus.Failed;
      state.customLocalizations.rest.getError = action.payload ?? null;
    });
    builder.addCase(createCustomLocalization.pending, (state, action) => {
      state.customLocalizations.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createCustomLocalization.fulfilled, (state, action) => {
      state.customLocalizations.rest.postStatus = HttpFetchStatus.Success;
      if (state.customLocalizations.data != null) {
        state.customLocalizations.data = updateDataList(
          action.payload,
          state.customLocalizations.data,
        );
        state.customLocalizations.selectedId = action.payload.id;
      }
    });
    builder.addCase(createCustomLocalization.rejected, (state, action) => {
      state.customLocalizations.rest.postStatus = HttpFetchStatus.Failed;
      state.customLocalizations.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchCustomLocalizationById.pending, (state, action) => {
      state.customLocalizations.rest.patchStatus = HttpFetchStatus.Loading;
      state.customLocalizations.rest.patchId = action.meta.arg.id;
    });
    builder.addCase(patchCustomLocalizationById.fulfilled, (state, action) => {
      state.customLocalizations.rest.patchStatus = HttpFetchStatus.Success;
      if (state.customLocalizations.data != null) {
        state.customLocalizations.data = updateDataList(
          action.payload,
          state.customLocalizations.data,
        );
      }
    });
    builder.addCase(patchCustomLocalizationById.rejected, (state, action) => {
      state.customLocalizations.rest.patchStatus = HttpFetchStatus.Failed;
      state.customLocalizations.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteCustomLocalizationById.pending, (state, action) => {
      state.customLocalizations.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteCustomLocalizationById.fulfilled, (state, action) => {
      state.customLocalizations.rest.deleteStatus = HttpFetchStatus.Success;
      if (state.customLocalizations.data != null) {
        const agreementIndex = state.customLocalizations.data.findIndex(
          (agreement) => agreement.id === action.payload,
        );
        if (agreementIndex !== -1) {
          state.customLocalizations.data.splice(agreementIndex, 1);
          state.customLocalizations.selectedId = null;
        }
      }
    });
    builder.addCase(deleteCustomLocalizationById.rejected, (state, action) => {
      state.customLocalizations.rest.deleteStatus = HttpFetchStatus.Failed;
      state.customLocalizations.rest.deleteError = action.payload ?? null;
    });

    builder.addCase(getNotificationSubscriptions.pending, (state, action) => {
      state.notifications.rest.getStatus = HttpFetchStatus.Loading;
      state.notifications.data = null;
    });
    builder.addCase(getNotificationSubscriptions.fulfilled, (state, action) => {
      state.notifications.rest.getStatus = HttpFetchStatus.Success;
      state.notifications.data = action.payload ?? null;
    });
    builder.addCase(getNotificationSubscriptions.rejected, (state, action) => {
      state.notifications.rest.getStatus = HttpFetchStatus.Failed;
      state.notifications.rest.getError = action.payload ?? null;
    });
    builder.addCase(createNotificationSubscription.pending, (state, action) => {
      state.notifications.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createNotificationSubscription.fulfilled, (state, action) => {
      state.notifications.rest.postStatus = HttpFetchStatus.Success;
      if (state.notifications.data != null) {
        state.notifications.data = updateDataList(action.payload, state.notifications.data);
        state.notifications.selectedNotification = null;
      }
    });
    builder.addCase(createNotificationSubscription.rejected, (state, action) => {
      state.notifications.rest.postStatus = HttpFetchStatus.Failed;
      state.notifications.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchNotificationSubscriptionById.pending, (state, action) => {
      state.notifications.rest.patchStatus = HttpFetchStatus.Loading;
      state.notifications.rest.patchId = action.meta.arg.id;
    });
    builder.addCase(patchNotificationSubscriptionById.fulfilled, (state, action) => {
      state.notifications.rest.patchStatus = HttpFetchStatus.Success;
      if (state.notifications.data != null) {
        state.notifications.data = updateDataList(action.payload, state.notifications.data);
      }
      state.notifications.selectedNotification = null;
    });
    builder.addCase(patchNotificationSubscriptionById.rejected, (state, action) => {
      state.notifications.rest.patchStatus = HttpFetchStatus.Failed;
      state.notifications.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteNotificationSubscriptionById.pending, (state, action) => {
      state.notifications.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteNotificationSubscriptionById.fulfilled, (state, action) => {
      state.notifications.rest.deleteStatus = HttpFetchStatus.Success;
      if (state.notifications.data != null) {
        const agreementIndex = state.notifications.data.findIndex(
          (agreement) => agreement.id === action.payload,
        );
        if (agreementIndex !== -1) {
          state.notifications.data.splice(agreementIndex, 1);
        }
      }
    });
    builder.addCase(deleteNotificationSubscriptionById.rejected, (state, action) => {
      state.notifications.rest.deleteStatus = HttpFetchStatus.Failed;
      state.notifications.rest.deleteError = action.payload ?? null;
    });

    builder.addCase(getUpdateVersions.pending, (state, action) => {
      state.versions.versions.rest.getStatus = HttpFetchStatus.Loading;
      state.versions.versions.data = null;
    });
    builder.addCase(getUpdateVersions.fulfilled, (state, action) => {
      state.versions.versions.rest.getStatus = HttpFetchStatus.Success;
      state.versions.versions.data = action.payload ?? null;
    });
    builder.addCase(getUpdateVersions.rejected, (state, action) => {
      state.versions.versions.rest.getStatus = HttpFetchStatus.Failed;
      state.versions.versions.rest.getError = action.payload ?? null;
    });
    builder.addCase(getTorizonPackages.pending, (state, action) => {
      state.versions.torizonPackages.rest.getStatus = HttpFetchStatus.Loading;
      state.versions.torizonPackages.data = null;
    });
    builder.addCase(getTorizonPackages.fulfilled, (state, action) => {
      state.versions.torizonPackages.rest.getStatus = HttpFetchStatus.Success;
      state.versions.torizonPackages.data = action.payload ?? null;
    });
    builder.addCase(getTorizonPackages.rejected, (state, action) => {
      state.versions.torizonPackages.rest.getStatus = HttpFetchStatus.Failed;
      state.versions.torizonPackages.rest.getError = action.payload ?? null;
    });
    builder.addCase(createUpdateVersion.pending, (state, action) => {
      state.versions.versions.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createUpdateVersion.fulfilled, (state, action) => {
      state.versions.versions.rest.postStatus = HttpFetchStatus.Success;
      if (state.versions.versions.data != null) {
        state.versions.versions.data = updateDataList(action.payload, state.versions.versions.data);
      }
    });
    builder.addCase(createUpdateVersion.rejected, (state, action) => {
      state.versions.versions.rest.postStatus = HttpFetchStatus.Failed;
      state.versions.versions.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchUpdateVersionById.pending, (state, action) => {
      state.versions.versions.rest.patchStatus = HttpFetchStatus.Loading;
      state.versions.versions.rest.patchId = action.meta.arg.id;
    });
    builder.addCase(patchUpdateVersionById.fulfilled, (state, action) => {
      state.versions.versions.rest.patchStatus = HttpFetchStatus.Success;
      if (state.versions.versions.data != null) {
        state.versions.versions.data = updateDataList(action.payload, state.versions.versions.data);
      }
    });
    builder.addCase(patchUpdateVersionById.rejected, (state, action) => {
      state.versions.versions.rest.patchStatus = HttpFetchStatus.Failed;
      state.versions.versions.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteUpdateVersionById.pending, (state, action) => {
      state.versions.versions.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteUpdateVersionById.fulfilled, (state, action) => {
      state.versions.versions.rest.deleteStatus = HttpFetchStatus.Success;
      if (state.versions.versions.data != null) {
        const versionIndex = state.versions.versions.data.findIndex((v) => v.id === action.payload);
        if (versionIndex !== -1) {
          state.versions.versions.data.splice(versionIndex, 1);
        }
      }
    });
    builder.addCase(deleteUpdateVersionById.rejected, (state, action) => {
      state.versions.versions.rest.deleteStatus = HttpFetchStatus.Failed;
      state.versions.versions.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(getVersionSets.pending, (state, action) => {
      state.versions.sets.rest.getStatus = HttpFetchStatus.Loading;
      state.versions.sets.data = null;
    });
    builder.addCase(getVersionSets.fulfilled, (state, action) => {
      state.versions.sets.rest.getStatus = HttpFetchStatus.Success;
      state.versions.sets.data = action.payload ?? null;
    });
    builder.addCase(getVersionSets.rejected, (state, action) => {
      state.versions.sets.rest.getStatus = HttpFetchStatus.Failed;
      state.versions.sets.rest.getError = action.payload ?? null;
    });
    builder.addCase(createVersionSet.pending, (state, action) => {
      state.versions.sets.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createVersionSet.fulfilled, (state, action) => {
      state.versions.sets.rest.postStatus = HttpFetchStatus.Success;
      if (state.versions.sets.data != null) {
        state.versions.sets.data = updateDataList(action.payload, state.versions.sets.data);
      }
    });
    builder.addCase(createVersionSet.rejected, (state, action) => {
      state.versions.sets.rest.postStatus = HttpFetchStatus.Failed;
      state.versions.sets.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchVersionSetById.pending, (state, action) => {
      state.versions.sets.rest.patchStatus = HttpFetchStatus.Loading;
      state.versions.sets.rest.patchId = action.meta.arg.id;
    });
    builder.addCase(patchVersionSetById.fulfilled, (state, action) => {
      state.versions.sets.rest.patchStatus = HttpFetchStatus.Success;
      if (state.versions.sets.data != null) {
        state.versions.sets.data = updateDataList(action.payload, state.versions.sets.data);
      }
    });
    builder.addCase(patchVersionSetById.rejected, (state, action) => {
      state.versions.sets.rest.patchStatus = HttpFetchStatus.Failed;
      state.versions.sets.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteVersionSetById.pending, (state, action) => {
      state.versions.sets.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteVersionSetById.fulfilled, (state, action) => {
      state.versions.sets.rest.deleteStatus = HttpFetchStatus.Success;
      if (state.versions.sets.data != null) {
        const versionIndex = state.versions.sets.data.findIndex((v) => v.id === action.payload);
        if (versionIndex !== -1) {
          state.versions.sets.data.splice(versionIndex, 1);
        }
      }
    });
    builder.addCase(deleteVersionSetById.rejected, (state, action) => {
      state.versions.sets.rest.deleteStatus = HttpFetchStatus.Failed;
      state.versions.sets.rest.deleteError = action.payload ?? null;
    });

    builder.addCase(getTorizonVersionSets.pending, (state) => {
      state.versions.torizonSets.rest.getStatus = HttpFetchStatus.Loading;
      state.versions.torizonSets.data = null;
    });
    builder.addCase(getTorizonVersionSets.fulfilled, (state, action) => {
      state.versions.torizonSets.rest.getStatus = HttpFetchStatus.Success;
      state.versions.torizonSets.data = action.payload ?? null;
    });
    builder.addCase(getTorizonVersionSets.rejected, (state, action) => {
      state.versions.torizonSets.rest.getStatus = HttpFetchStatus.Failed;
      state.versions.torizonSets.rest.getError = action.payload ?? null;
    });
    builder.addCase(createTorizonVersionSet.pending, (state) => {
      state.versions.torizonSets.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createTorizonVersionSet.fulfilled, (state, action) => {
      state.versions.torizonSets.rest.postStatus = HttpFetchStatus.Success;
      if (state.versions.torizonSets.data != null) {
        state.versions.torizonSets.data = updateDataList(
          action.payload,
          state.versions.torizonSets.data,
        );
      }
    });
    builder.addCase(createTorizonVersionSet.rejected, (state, action) => {
      state.versions.torizonSets.rest.postStatus = HttpFetchStatus.Failed;
      state.versions.torizonSets.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchTorizonVersionSetById.pending, (state, action) => {
      state.versions.torizonSets.rest.patchStatus = HttpFetchStatus.Loading;
      state.versions.torizonSets.rest.patchId = action.meta.arg.id;
    });
    builder.addCase(patchTorizonVersionSetById.fulfilled, (state, action) => {
      state.versions.torizonSets.rest.patchStatus = HttpFetchStatus.Success;
      if (state.versions.torizonSets.data != null) {
        state.versions.torizonSets.data = updateDataList(
          action.payload,
          state.versions.torizonSets.data,
        );
      }
    });
    builder.addCase(patchTorizonVersionSetById.rejected, (state, action) => {
      state.versions.torizonSets.rest.patchStatus = HttpFetchStatus.Failed;
      state.versions.torizonSets.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteTorizonVersionSetById.pending, (state) => {
      state.versions.torizonSets.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteTorizonVersionSetById.fulfilled, (state, action) => {
      state.versions.torizonSets.rest.deleteStatus = HttpFetchStatus.Success;
      if (state.versions.torizonSets.data != null) {
        const setIndex = state.versions.torizonSets.data.findIndex((v) => v.id === action.payload);
        if (setIndex !== -1) {
          state.versions.torizonSets.data.splice(setIndex, 1);
        }
      }
    });
    builder.addCase(deleteTorizonVersionSetById.rejected, (state, action) => {
      state.versions.torizonSets.rest.deleteStatus = HttpFetchStatus.Failed;
      state.versions.torizonSets.rest.deleteError = action.payload ?? null;
    });

    builder.addCase(getAssignableRobots.pending, (state) => {
      state.versions.assignableRobots.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getAssignableRobots.fulfilled, (state, action) => {
      state.versions.assignableRobots.rest.getStatus = HttpFetchStatus.Success;
      state.versions.assignableRobots.data = action.payload ?? null;
    });
    builder.addCase(getAssignableRobots.rejected, (state, action) => {
      state.versions.assignableRobots.rest.getStatus = HttpFetchStatus.Failed;
      state.versions.assignableRobots.rest.getError = action.payload ?? null;
    });
    builder.addCase(assignTorizonVersionSet.pending, (state) => {
      state.versions.assignableRobots.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(assignTorizonVersionSet.fulfilled, (state, action) => {
      state.versions.assignableRobots.rest.postStatus = HttpFetchStatus.Success;
      // Reflect the assignment locally so the table's current-set column and the
      // per-set robot counts update without a refetch.
      if (state.versions.assignableRobots.data != null) {
        state.versions.assignableRobots.data = state.versions.assignableRobots.data.map((robot) =>
          action.payload.robotIds.includes(robot.id)
            ? { ...robot, versionSetId: action.payload.id }
            : robot,
        );
      }
      if (state.versions.torizonSets.data != null) {
        state.versions.torizonSets.data = state.versions.torizonSets.data.map((versionSet) => ({
          ...versionSet,
          robotCount:
            state.versions.assignableRobots.data?.filter((r) => r.versionSetId === versionSet.id)
              .length ?? versionSet.robotCount,
        }));
      }
    });
    builder.addCase(assignTorizonVersionSet.rejected, (state, action) => {
      state.versions.assignableRobots.rest.postStatus = HttpFetchStatus.Failed;
      state.versions.assignableRobots.rest.postError = action.payload ?? null;
    });

    builder.addCase(validateUserInvite.pending, (state, action) => {
      state.userInviteData.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(validateUserInvite.fulfilled, (state, action) => {
      state.userInviteData.rest.getStatus = HttpFetchStatus.Success;
      state.userInviteData.organizationId = action.payload.organizationId;
    });
    builder.addCase(validateUserInvite.rejected, (state, action) => {
      state.userInviteData.rest.getStatus = HttpFetchStatus.Failed;
      state.userInviteData.rest.getError = action.payload ?? null;
    });
    builder.addCase(registerUser.pending, (state, action) => {
      state.userInviteData.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(registerUser.fulfilled, (state, action) => {
      state.userInviteData.rest.postStatus = HttpFetchStatus.Success;
    });
    builder.addCase(registerUser.rejected, (state, action) => {
      state.userInviteData.rest.postStatus = HttpFetchStatus.Failed;
      state.userInviteData.rest.postError = action.payload ?? null;
    });
    builder.addCase(getFrontendParams.pending, (state, action) => {
      state.frontendParams.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getFrontendParams.fulfilled, (state, action) => {
      state.frontendParams.getStatus = HttpFetchStatus.Success;
      state.frontendParams.data = action.payload;
    });
    builder.addCase(getFrontendParams.rejected, (state, action) => {
      state.frontendParams.getStatus = HttpFetchStatus.Failed;
      state.frontendParams.getError = action.payload ?? null;
    });

    builder.addCase(postSubscribeToOrganizationById.pending, (state, action) => {
      state.user.rest.postSubscribeOrganizationStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(postSubscribeToOrganizationById.fulfilled, (state, action) => {
      state.user.rest.postSubscribeOrganizationStatus = HttpFetchStatus.Success;
      state.user.data.organizationSubscriptions = action.payload;
    });
    builder.addCase(postSubscribeToOrganizationById.rejected, (state, action) => {
      state.user.rest.postSubscribeOrganizationStatus = HttpFetchStatus.Failed;
      state.user.rest.postSubscribeOrganizationError = action.payload ?? null;
    });
    builder.addCase(postSubscribeToRobotById.pending, (state, action) => {
      state.user.rest.postSubscribeRobotStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(postSubscribeToRobotById.fulfilled, (state, action) => {
      state.user.rest.postSubscribeRobotStatus = HttpFetchStatus.Success;
      state.user.data.robotSubscriptions = action.payload;
    });
    builder.addCase(postSubscribeToRobotById.rejected, (state, action) => {
      state.user.rest.postSubscribeRobotStatus = HttpFetchStatus.Failed;
      state.user.rest.postSubscribeRobotError = action.payload ?? null;
    });

    builder.addCase(getReportDataSets.pending, (state, action) => {
      state.notifications.rest.getReportDataStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getReportDataSets.fulfilled, (state, action) => {
      state.notifications.rest.getReportDataStatus = HttpFetchStatus.Success;
      state.notifications.reportDataSets = action.payload;
    });
    builder.addCase(getReportDataSets.rejected, (state, action) => {
      state.notifications.rest.getReportDataStatus = HttpFetchStatus.Failed;
      state.notifications.rest.getReportDataError = action.payload ?? null;
    });
  },
});

export const {
  resetGeneralState,
  localLogin,
  logout,
  resetLoginState,
  resetUserRestStatus,
  setCustomUserRole,
  resetAgreementsRestState,
  resetAgreementsState,
  resetAssignableRobotsRestState,
  resetTorizonVersionSetsRestState,
  resetVersionSetsRestState,
  resetVersionVersionsRestState,
  setAgreementFileUploadProgress,
  setSelectedAgreementId,
  resetCustomLocalizationRestStatus,
  setSelectedCustomLocalizationId,
  resetNotificationRestState,
  resetReportData,
  setSelectedNotification,
  resetRegisterRestState,
  setIsAdvancedModeEnabled,
  commitTwoFactorLogin,
  resetTwoFactorRestState,
} = generalSlice.actions;

export default generalSlice.reducer;
